import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, CalendarClock, AlertCircle } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useFormularioDistribuido } from "@/hooks/useFormularioDistribuido";
import { useUserFormularios } from "@/hooks/useUserFormularios";
import { PageLoading } from "@/components/ui/page-loading";
import { FormularioRenderer } from "@/components/Formularios/FormularioRenderer";

interface UserFormularioRendererProps {
  notificacao: any;
  onVoltar: () => void;
}

export function UserFormularioRenderer({ notificacao, onVoltar }: UserFormularioRendererProps) {
  const formularioId = notificacao.formulario_id;
  const distribuicao = notificacao.distribuicao;
  const respostaExistente = notificacao.resposta;
  
  const [respostaId, setRespostaId] = useState<string | undefined>(respostaExistente?.id);
  
  const { marcarComoRespondida } = useUserFormularios();

  // Buscar formulário e campos usando o hook de formulários distribuídos
  const { formulario, campos, isLoading } = useFormularioDistribuido(formularioId, notificacao.id);

  const handleConcluir = async (respostaIdFinal: string) => {
    await marcarComoRespondida({
      notificacaoId: notificacao.id,
      respostaId: respostaIdFinal,
    });
    onVoltar();
  };

  if (isLoading) {
    return <PageLoading />;
  }

  if (!formulario || !campos) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-muted-foreground">
            Formulário não encontrado
          </div>
        </CardContent>
      </Card>
    );
  }

  const prazoVencido = distribuicao?.data_limite && new Date(distribuicao.data_limite) < new Date();
  const isVisualizacao = notificacao.respondida || prazoVencido;

  return (
    <div className="flex justify-center py-4 px-4">
      <div className="w-full max-w-4xl space-y-1">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={onVoltar}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          
          <div className="flex gap-2">
            {distribuicao?.obrigatorio && (
              <Badge variant="destructive">Obrigatório</Badge>
            )}
            {notificacao.respondida && (
              <Badge variant="secondary">Respondido</Badge>
            )}
          </div>
        </div>

      {distribuicao?.data_limite && (
        <Card className={prazoVencido ? "border-red-500" : ""}>
          <CardContent className="py-3">
            <div className="flex items-center gap-2">
              <CalendarClock className={`h-4 w-4 ${prazoVencido ? "text-red-500" : "text-muted-foreground"}`} />
              <span className={prazoVencido ? "text-red-500 font-medium" : "text-muted-foreground"}>
                Prazo: {format(new Date(distribuicao.data_limite), "dd/MM/yyyy HH:mm", { locale: ptBR })}
              </span>
              {prazoVencido && (
                <>
                  <AlertCircle className="h-4 w-4 text-red-500" />
                  <span className="text-red-500 font-medium">Vencido</span>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {distribuicao?.mensagem_personalizada && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Mensagem do Responsável</CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <p className="text-muted-foreground">{distribuicao.mensagem_personalizada}</p>
          </CardContent>
        </Card>
      )}

      {prazoVencido && !notificacao.respondida ? (
        <Card className="border-red-500">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <p className="text-lg font-medium text-red-500">Prazo de resposta expirado</p>
              <p className="text-muted-foreground mt-2">
                Não é mais possível responder este formulário
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <FormularioRenderer
          formularioId={formulario.id}
          formularioTitulo={formulario.titulo}
          formularioDescricao={formulario.descricao}
          campos={campos}
          respostaId={respostaId}
          isPublic={true}
          onCancel={onVoltar}
          onSubmitSuccess={handleConcluir}
          solicitacaoNumero={distribuicao?.metadata?.solicitacao_numero}
          solicitacaoTitulo={distribuicao?.metadata?.solicitacao_titulo}
        />
      )}
      </div>
    </div>
  );
}
